<template>
  <section class="reports-table-section">
    <div class="app-table-wrap reports-table-wrap">
      <div
        class="app-table-scroll"
        :class="{ 'reports-table-scroll--sticky-head': stickyHeader }"
      >
        <table class="app-data-table reports-data-table">
          <thead :class="{ 'reports-data-table-head--sticky': stickyHeader }">
            <tr>
              <th
                v-for="header in normalizedHeaders"
                :key="header.key"
                :class="headerClass(header)"
              >
                {{ header.name }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="normalizedHeaders.length === 0 || normalizedRows.length === 0">
              <td :colspan="colspan" class="app-table-empty">No hay registros para mostrar en este reporte.</td>
            </tr>

            <tr v-for="row in normalizedRows" :key="row.row_id" :class="rowClass(row)">
              <template v-for="header in normalizedHeaders">
                <td
                  v-if="shouldRenderCell(row, header)"
                  :key="`${row.row_id}:${header.key}`"
                  :colspan="cellColspan(row, header)"
                  :class="cellClass(header, row)"
                >
                  <template v-if="header.type === 'pivot'">
                    <router-link
                      v-if="shouldLinkPivot(row, header)"
                      :to="pivotLinkTo(row, header)"
                      class="reports-table-link"
                    >
                      {{ pivotCellValue(row, header) }}
                    </router-link>
                    <span v-else>{{ pivotCellValue(row, header) }}</span>
                  </template>
                  <template v-else-if="header.type === 'period'">
                    <button
                      v-if="canOpenPeriodDetail(row, header)"
                      type="button"
                      class="reports-table-period-button"
                      @click.stop="emitPeriodCellClick(row, header)"
                    >
                      {{ formatAmount(periodCellAmount(row, header)) }}
                    </button>
                    <span v-else>{{ formatAmount(periodCellAmount(row, header)) }}</span>
                  </template>
                  <template v-else-if="header.type === 'total'">
                    <button
                      v-if="canOpenTotalDetail(row)"
                      type="button"
                      class="reports-table-period-button app-strong"
                      @click.stop="emitTotalCellClick(row)"
                    >
                      {{ formatAmount(row.total) }}
                    </button>
                    <span v-else class="app-strong">{{ formatAmount(row.total) }}</span>
                  </template>
                </td>
              </template>
            </tr>

            <tr v-if="showTotalsRow" class="reports-table-totals-row">
              <td
                v-for="header in normalizedHeaders"
                :key="`totals:${header.key}`"
                :class="cellClass(header, { level: 1 })"
              >
                <span v-if="header.type === 'pivot' && header.level === 1" class="app-strong">Total</span>
                <template v-else-if="header.type === 'period'">
                  <button
                    v-if="canOpenPeriodTotalDetail(header)"
                    type="button"
                    class="reports-table-period-button app-strong"
                    @click.stop="emitPeriodTotalCellClick(header)"
                  >
                    {{ formatAmount(periodTotals[header.index] || 0) }}
                  </button>
                  <span v-else class="app-strong">{{ formatAmount(periodTotals[header.index] || 0) }}</span>
                </template>
                <template v-else-if="header.type === 'total'">
                  <button
                    v-if="canOpenGrandTotalDetail()"
                    type="button"
                    class="reports-table-period-button app-strong"
                    @click.stop="emitGrandTotalCellClick()"
                  >
                    {{ formatAmount(grandTotal) }}
                  </button>
                  <span v-else class="app-strong">{{ formatAmount(grandTotal) }}</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import {
  REPORTS_SORT_DIRECTION_ASC,
  REPORTS_SORT_DIRECTION_NONE,
  normalizeReportsSortDirection,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';

export default {
  name: 'ReportsTable',
  emits: ['period-cell-click'],
  props: {
    table: {
      type: Object,
      default: () => ({}),
    },
    allowTotalsDetail: {
      type: Boolean,
      default: false,
    },
    sortDirection: {
      type: String,
      default: REPORTS_SORT_DIRECTION_NONE,
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    normalizedHeaders() {
      const headers = Array.isArray(this.table?.headers) ? this.table.headers : [];

      return headers.map((header, index) => {
        const type = String(header?.type || '').trim().toLowerCase();
        const level = Number(header?.level || 1);
        const headerIndex = Number(header?.index || 0);

        return {
          key: `${type}:${level}:${headerIndex}:${index}`,
          type,
          level,
          index: headerIndex,
          name: String(header?.name || '').trim() || 'Columna',
          code: String(header?.code || '').trim(),
          start: String(header?.start || '').trim(),
          end: String(header?.end || '').trim(),
        };
      });
    },
    normalizedSortDirection() {
      return normalizeReportsSortDirection(this.sortDirection);
    },
    sortedSourceRows() {
      return this.sortRowsByTotal(
        Array.isArray(this.table?.rows) ? this.table.rows : [],
        this.normalizedSortDirection
      );
    },
    normalizedRows() {
      const sourceRows = this.sortedSourceRows;
      const flattenedRows = [];

      const walkRows = (rows, inheritedPivots = {}, inheritedPivotDataByLevel = {}) => {
        (Array.isArray(rows) ? rows : []).forEach((row, index) => {
          if (!row || typeof row !== 'object') {
            return;
          }

          const level = Number(row.level || 1);
          const pivotName = String(row?.pivot?.name || '').trim();
          const pivotCode = String(row?.pivot?.code || '').trim().toLowerCase();
          const pivotId = Number(row?.pivot?.id || 0);
          const pivots = {
            ...inheritedPivots,
            [level]: pivotName,
          };
          const pivotDataByLevel = {
            ...inheritedPivotDataByLevel,
            [level]: {
              code: pivotCode,
              id: pivotId,
              name: pivotName,
            },
          };

          const children = Array.isArray(row.children) ? row.children : [];

          // En reportes con dos pivotes se listan primero los hijos (nivel 2)
          // y despues la fila de total del grupo (nivel 1).
          if (children.length > 0) {
            walkRows(children, pivots, pivotDataByLevel);
          }

          flattenedRows.push({
            row_id: String(row.row_id || `row-${flattenedRows.length + 1}-${index}`),
            level,
            pivots,
            pivotDataByLevel,
            values: Array.isArray(row.values) ? row.values : [],
            elementIds: Array.isArray(row.element_ids) ? row.element_ids : [],
            periodElementIds: Array.isArray(row.period_element_ids) ? row.period_element_ids : [],
            total: Number(row.total || 0),
          });
        });
      };

      walkRows(sourceRows);
      return flattenedRows;
    },
    periodTotals() {
      return Array.isArray(this.table?.period_totals) ? this.table.period_totals : [];
    },
    grandTotal() {
      return Number(this.table?.grand_total || 0);
    },
    showTotalsRow() {
      if (this.normalizedRows.length === 0) {
        return false;
      }

      return this.normalizedHeaders.some((header) => {
        return header.type === 'period' || header.type === 'total';
      });
    },
    hasSecondPivot() {
      const pivotHeaderCount = this.normalizedHeaders.filter((header) => header.type === 'pivot').length;
      return pivotHeaderCount > 1;
    },
    periodHeaderCount() {
      return this.normalizedHeaders.filter((header) => header.type === 'period').length;
    },
    shouldCenterHeaderTitles() {
      if (this.periodHeaderCount === 0) {
        return false;
      }

      return this.periodHeaderCount <= 8;
    },
    colspan() {
      return Math.max(this.normalizedHeaders.length, 1);
    },
  },
  methods: {
    toSortableTotal(row) {
      return Number(row?.total || 0);
    },
    sortRowsByTotal(rows, direction = REPORTS_SORT_DIRECTION_NONE) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const normalizedDirection = normalizeReportsSortDirection(direction);
      const withChildren = sourceRows.map((row, index) => {
        const source = row && typeof row === 'object' ? row : {};
        const children = this.sortRowsByTotal(source.children, normalizedDirection);

        return {
          ...source,
          children,
          __sortIndex: index,
        };
      });

      if (normalizedDirection !== REPORTS_SORT_DIRECTION_NONE) {
        const multiplier = normalizedDirection === REPORTS_SORT_DIRECTION_ASC ? 1 : -1;
        withChildren.sort((left, right) => {
          const leftTotal = this.toSortableTotal(left);
          const rightTotal = this.toSortableTotal(right);

          if (leftTotal === rightTotal) {
            return Number(left.__sortIndex || 0) - Number(right.__sortIndex || 0);
          }

          return (leftTotal - rightTotal) * multiplier;
        });
      }

      return withChildren.map(({ __sortIndex, ...row }) => row);
    },
    formatAmount(value) {
      return formatCurrency(Number(value || 0), { currency: 'MXN' });
    },
    headerClass(header) {
      return {
        'app-text-right': !this.shouldCenterHeaderTitles && (header.type === 'period' || header.type === 'total'),
        'reports-table-header--centered': this.shouldCenterHeaderTitles && (header.type === 'period' || header.type === 'total'),
      };
    },
    isGroupTotalRow(row) {
      return this.hasSecondPivot && Number(row?.level || 0) === 1;
    },
    rowClass(row) {
      return {
        'reports-table-row--group-total': this.isGroupTotalRow(row),
      };
    },
    shouldMergePivotColumns(row, header) {
      return this.isGroupTotalRow(row)
        && this.hasSecondPivot
        && header.type === 'pivot'
        && header.level === 1;
    },
    shouldSkipPivotColumn(row, header) {
      return this.isGroupTotalRow(row)
        && this.hasSecondPivot
        && header.type === 'pivot'
        && header.level === 2;
    },
    shouldRenderCell(row, header) {
      return !this.shouldSkipPivotColumn(row, header);
    },
    cellColspan(row, header) {
      if (this.shouldMergePivotColumns(row, header)) {
        return 2;
      }

      return 1;
    },
    groupTotalLabel(row) {
      const primaryName = String(row?.pivots?.[1] || '').trim();
      return primaryName !== '' ? `Total ${primaryName}` : 'Total';
    },
    pivotMeta(row, header) {
      const level = Number(header?.level || 1);
      const source = row?.pivotDataByLevel?.[level] || null;

      return {
        code: String(source?.code || '').trim().toLowerCase(),
        id: Number(source?.id || 0),
      };
    },
    isCustomerPivotCell(row, header) {
      if (header.type !== 'pivot') {
        return false;
      }

      return this.pivotMeta(row, header).code === 'customer';
    },
    shouldLinkPivot(row, header) {
      if (header.type !== 'pivot' || this.isGroupTotalRow(row)) {
        return false;
      }

      const meta = this.pivotMeta(row, header);
      if (meta.id <= 0) {
        return false;
      }

      return meta.code === 'customer' || meta.code === 'sale';
    },
    pivotLinkTo(row, header) {
      const meta = this.pivotMeta(row, header);

      if (meta.code === 'customer') {
        return {
          name: 'customerDetail',
          params: { customerId: meta.id },
        };
      }

      if (meta.code === 'sale') {
        return {
          name: 'saleDetail',
          params: { saleId: meta.id },
        };
      }

      return { name: 'reportsSales' };
    },
    pivotCellValue(row, header) {
      if (header.type !== 'pivot') {
        return '';
      }

      if (!this.hasSecondPivot) {
        return row.pivots[header.level] || '';
      }

      if (this.shouldMergePivotColumns(row, header)) {
        return this.groupTotalLabel(row);
      }

      return row.pivots[header.level] || '';
    },
    periodCellAmount(row, header) {
      if (header.type !== 'period') {
        return 0;
      }

      return Number(row?.values?.[header.index] || 0);
    },
    periodCellElementIds(row, header) {
      if (header.type !== 'period') {
        return [];
      }

      const raw = row?.periodElementIds?.[header.index] || [];
      return this.normalizeElementIds(raw);
    },
    canOpenPeriodDetail(row, header) {
      if (header.type !== 'period') {
        return false;
      }

      if (this.isGroupTotalRow(row) && !this.allowTotalsDetail) {
        return false;
      }

      return this.periodCellElementIds(row, header).length > 0;
    },
    emitPeriodCellClick(row, header) {
      const elementIds = this.periodCellElementIds(row, header);
      if (elementIds.length === 0) {
        return;
      }

      const primaryPivotId = Number(row?.pivotDataByLevel?.[1]?.id || 0);
      const secondaryPivotId = Number(row?.pivotDataByLevel?.[2]?.id || 0);
      const primaryPivotName = String(row?.pivots?.[1] || '').trim();
      const secondaryPivotName = String(row?.pivots?.[2] || '').trim();
      const columnLabel = String(header?.name || '').trim();

      this.$emit('period-cell-click', {
        element_ids: elementIds,
        period_index: Number(header?.index || 0),
        period_code: String(header?.code || '').trim(),
        period_start: String(header?.start || '').trim(),
        period_end: String(header?.end || '').trim(),
        primary_pivot_name: primaryPivotName,
        secondary_pivot_name: secondaryPivotName,
        column_label: columnLabel,
        primary_id: primaryPivotId > 0 ? primaryPivotId : null,
        secondary_id: secondaryPivotId > 0 ? secondaryPivotId : null,
      });
    },
    normalizeElementIds(raw) {
      if (!Array.isArray(raw)) {
        return [];
      }

      return raw
        .map((value) => Number(value || 0))
        .filter((value) => Number.isInteger(value) && value > 0)
        .filter((value, index, source) => source.indexOf(value) === index);
    },
    rowTotalElementIds(row) {
      return this.normalizeElementIds(row?.element_ids || row?.elementIds || []);
    },
    canOpenTotalDetail(row) {
      if (!this.allowTotalsDetail) {
        return false;
      }

      return this.rowTotalElementIds(row).length > 0;
    },
    emitTotalCellClick(row) {
      const elementIds = this.rowTotalElementIds(row);
      if (elementIds.length === 0) {
        return;
      }

      const primaryPivotName = String(row?.pivots?.[1] || '').trim();
      const secondaryPivotName = String(row?.pivots?.[2] || '').trim();

      this.$emit('period-cell-click', {
        element_ids: elementIds,
        primary_pivot_name: primaryPivotName,
        secondary_pivot_name: secondaryPivotName,
        column_label: 'Total',
        total_scope: 'row_total',
      });
    },
    periodTotalElementIds(header) {
      if (header?.type !== 'period') {
        return [];
      }

      const index = Number(header?.index || 0);
      const source = Array.isArray(this.table?.period_total_element_ids)
        ? this.table.period_total_element_ids
        : [];

      return this.normalizeElementIds(source[index] || []);
    },
    canOpenPeriodTotalDetail(header) {
      if (!this.allowTotalsDetail || header?.type !== 'period') {
        return false;
      }

      return this.periodTotalElementIds(header).length > 0;
    },
    emitPeriodTotalCellClick(header) {
      const elementIds = this.periodTotalElementIds(header);
      if (elementIds.length === 0) {
        return;
      }

      this.$emit('period-cell-click', {
        element_ids: elementIds,
        period_index: Number(header?.index || 0),
        period_code: String(header?.code || '').trim(),
        period_start: String(header?.start || '').trim(),
        period_end: String(header?.end || '').trim(),
        column_label: String(header?.name || '').trim(),
        total_scope: 'period_total',
      });
    },
    grandTotalElementIds() {
      const source = Array.isArray(this.table?.grand_total_element_ids)
        ? this.table.grand_total_element_ids
        : [];

      return this.normalizeElementIds(source);
    },
    canOpenGrandTotalDetail() {
      if (!this.allowTotalsDetail) {
        return false;
      }

      return this.grandTotalElementIds().length > 0;
    },
    emitGrandTotalCellClick() {
      const elementIds = this.grandTotalElementIds();
      if (elementIds.length === 0) {
        return;
      }

      this.$emit('period-cell-click', {
        element_ids: elementIds,
        column_label: 'Total',
        total_scope: 'grand_total',
      });
    },
    cellClass(header, row) {
      return {
        'app-text-right': header.type === 'period' || header.type === 'total',
        'app-strong': (header.type === 'pivot' && header.level === 1 && Number(row?.level || 1) === 1)
          || this.isCustomerPivotCell(row, header),
        'reports-table-cell--group-total': this.isGroupTotalRow(row),
      };
    },
  },
};
</script>

<style scoped>
.reports-table-row--group-total td {
  background: rgba(0, 112, 205, 0.05);
}

.reports-table-cell--group-total {
  font-weight: 600;
}

.reports-table-cell--group-total.app-text-right {
  color: #0f5da8;
}

.reports-table-totals-row td {
  background: rgba(11, 63, 114, 0.16);
  border-top: 2px solid rgba(11, 63, 114, 0.55);
  color: #0b3f72;
  font-weight: 700;
}

.reports-table-link {
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
}

.reports-table-link:hover,
.reports-table-link:focus-visible {
  text-decoration: underline;
}

.reports-table-period-button {
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: inherit;
  padding: 0;
  text-decoration: none;
}

.reports-table-period-button:hover,
.reports-table-period-button:focus-visible {
  text-decoration: none;
}

.reports-data-table thead th.reports-table-header--centered {
  text-align: center;
}

.reports-table-scroll--sticky-head {
  max-height: min(56vh, 560px);
  overflow-y: auto;
}

.reports-data-table-head--sticky th {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #eaf2fb;
  box-shadow: inset 0 -1px 0 rgba(153, 152, 153, 0.3);
}
</style>
